import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Container, Row, Button, Col, Modal, Form } from 'react-bootstrap'
import { useNavigate } from 'react-router';
import AppLoader from '../../components/common/AppLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import AppSearch from '../../components/common/AppSearch';
import { Checkbox, Pagination, Select } from 'antd';
import { useAuth } from '../../contexts/auth';
import { Project } from '../../models/Project';
import { Projects } from '../../api/Projects';
import ProjectItem from '../../components/list-items/ProjectItem';
import { accessLevelOptions } from '../../utils';
import { CheckboxChangeEvent } from 'antd/es/checkbox';


export default function ProjectsList() {
  const [projects, setProjects] = useState<Project[]>();
  const [error, setError] = useState(false);

  const [newProject, setNewProject] = useState(new Project());
  const [projectToDelete, setProjectToDelete] = useState<Project>();
  const [validated, setValidated] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [accessLevelFilter, setAccessLevelFilter] = useState<string>();
  const [myProjects, setMyProjects] = useState(false);
  const [sortDesc, setSortDesc] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    return projects
      ?.filter(project => project.name.toLowerCase().includes(searchText.toLowerCase()))
      .filter(project => !myProjects || project.owner!.id === loggedUser?.sub)
      .filter(project => !accessLevelFilter || project.accessLevel === accessLevelFilter)
      .sort((a, b) => sortDesc ? b.id! - a.id! : a.id! - b.id!)
      .slice((currentPage - 1) * itemsPerPage, itemsPerPage * currentPage);
  }, [projects, searchText, myProjects, loggedUser, accessLevelFilter, sortDesc, itemsPerPage, currentPage]);

  const handleDeleteModalClose = () => { setShowDeleteModal(false); setProjectToDelete(undefined); }
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setValidated(false);
  }

  useEffect(() => {
    getProjects();
    // eslint-disable-next-line
  }, []);

  const getProjects = () => {
    Projects.getProjects()
      .then(
        ({ data: projects }) => setProjects(projects),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('projects-error', message, `Projects Error ${status ? `(${status})` : ''}`, 'danger');
          setProjects([]);
          setError(true);
        }
      );
  }

  const navigateToEdit = (project: Project) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    navigate(`/projects/${project.id}/edit`);
  }
  const navigateToDetail = (project: Project) => () => navigate(`/projects/${project.id}`);
  const changeSortMode = () => setSortDesc(!sortDesc);
  const changePage = (page: number) => setCurrentPage(page);
  const handleMyProjectsChange = (evt: CheckboxChangeEvent) => setMyProjects(evt.target.checked);

  const handleItemsPerPageChange = (page: number, size: number) => {
    changePage(page);
    setItemsPerPage(size);
  }

  const openDeleteModal = (project: Project) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setProjectToDelete(project);
  }

  const reset = () => {
    setProjects(undefined);
    getProjects();
  }

  const openCreateModal = () => setShowEditModal(true);

  const deleteProject = () => {
    showLoader();

    Projects.deleteProject(projectToDelete!.id!)
      .then(
        () => {
          showToast('project-delete-success', 'Project deleted successfully.', 'Project Success', 'success');
          hideLoader();
          handleDeleteModalClose();
          reset();
        },
        () => {
          showToast('project-delete-error', 'Could not delete project.', 'Project Error', 'danger');
          hideLoader();
        }
      );
  }

  const handleFilterChange = (filter: 'search' | 'accessLevel') => (evt: any) => {
    switch (filter) {
      case 'search':
      default:
        setSearchText(evt);
        break;

      case 'accessLevel':
        setAccessLevelFilter(evt);
        break;
    }
  }

  const handleFieldChange = (field: keyof Project) => (evt: any) => {
    const value = field === "accessLevel" ? evt : evt.target.value;
    setNewProject({
      ...newProject,
      [field]: value,
    });
  }

  const handleProjectSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader();

      Projects.createProject(newProject)
        .then(
          () => {
            hideLoader();
            showToast("projects-create-success", "Project created successfully", 'Project Success', 'success');
            reset();
            handleEditModalClose();
          },
          (err) => {
            hideLoader();
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast(`projects-create-error`, message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }

  const renderProjects = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured, please try again later.</span>
      </Alert>
    );

    return filtered?.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No projects found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            filtered?.map(project => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={project.id}>
                <ProjectItem
                  project={project}
                  onClick={navigateToDetail(project)}
                  onProjectDelete={openDeleteModal(project)}
                  onProjectEdit={navigateToEdit(project)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return projects
      ? renderProjects()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={300} />
        </div>
      );
  }

  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Projects List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="success" onClick={openCreateModal}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="ms-2">Create Project</span>
            </Button>
          </Col>
        </Row>
        <Row>
          <Col md={3} className="mb-2">
            <AppSearch placeholder="Search project..." value={searchText} onChange={handleFilterChange('search')} />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              placeholder="Select privacy level"
              value={accessLevelFilter}
              allowClear
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Checkbox onChange={handleMyProjectsChange}>My Projects</Checkbox>
            {/* <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox> */}
          </Col>
          <Col>
            <Pagination
              total={filtered?.length}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
              className="d-flex justify-content-center"
            />
          </Col>
          <Col md={3} className="text-end">
            <Button size="sm" variant="light" className="px-4 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Container className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete project <b>{projectToDelete && projectToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteModalClose}>
            Close
          </Button>
          <Button variant="danger" size="sm" onClick={deleteProject}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleEditModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Create Project</Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleProjectSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="project-name">
              <Form.Label>Name</Form.Label>
              <Form.Control size="sm" placeholder="Project Name" value={newProject.name} onChange={handleFieldChange("name")} required />
              <Form.Control.Feedback type="invalid">Project Name is required.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="project-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} size="sm" placeholder="Project Description" value={newProject.description} onChange={handleFieldChange("description")} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="project-access-level">
              <Form.Label>Privacy Level</Form.Label>
              <Select
                allowClear
                placeholder="Select privacy level"
                value={newProject.accessLevel}
                onChange={handleFieldChange('accessLevel')}
                className="w-100"
              >
                {accessLevelOptions
                  .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                  .map((opt, index) => (
                    <Select.Option key={index} value={opt.value} label={opt.label}>
                      <div>
                        <FontAwesomeIcon icon={opt.icon} fixedWidth />
                        <span className="ms-2">{opt.label}</span>
                      </div>
                    </Select.Option>
                  ))}
              </Select>
              <Form.Control.Feedback type="invalid">Project Privacy Level is required.</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" size="sm" className="border" onClick={handleEditModalClose}>Close</Button>
            <Button variant="success" size="sm" type="submit">Confirm</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}